// components/EmailTemplates/EmailTemplateList.js
import React, {useEffect, useState} from "react";
import {Box, VStack, useColorModeValue, useDisclosure, useToast} from "@chakra-ui/react";
import {useNavigate} from "react-router-dom";

// Import components
import EmailTemplateHeader from "./EmailTemplateHeader";
import StatsCards from "./StatsCards";
import TemplateFilters from "./TemplateFilters";
import TemplateTable from "./TemplateTable";
import TemplateFormModal from "./TemplateFormModal";
import TestEmailModal from "./TestEmailModal";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import Pagination from "../Common/Pagination/Pagination";

// Import services and constants
import {EmailTemplateApi} from "./services/EmailTemplateApi";
import { DEFAULT_TEMPLATE_TYPES, getTemplateTypesWithFallback } from "./constants/emailConstants";

export const EmailTemplateList = () => {
    // State management
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [templateTypes, setTemplateTypes] = useState([]);

    // Pagination and filtering
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [isDefaultFilter, setIsDefaultFilter] = useState('');

    // Modal states
    const {isOpen: isFormOpen, onOpen: openForm, onClose: closeForm} = useDisclosure();
    const {isOpen: isTestOpen, onOpen: openTest, onClose: closeTest} = useDisclosure();
    const {isOpen: isDeleteOpen, onOpen: openDelete, onClose: closeDelete} = useDisclosure();

    // Current selections
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateToDelete, setTemplateToDelete] = useState(null);

    // Loading states
    const [formLoading, setFormLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const navigate = useNavigate();
    const toast = useToast();

    // Color mode values
    const textColor = useColorModeValue("gray.800", "white");
    const bgColor = useColorModeValue("white", "gray.800");

    // API calls
    const fetchTemplates = async () => {
        try {
            setLoading(true);

            const params = {
                page: currentPage,
                limit,
                ...(searchTerm && {search: searchTerm}),
                ...(statusFilter && {status: statusFilter}),
                ...(typeFilter && {type: typeFilter}),
                ...(isDefaultFilter !== '' && {isDefault: isDefaultFilter === 'true'}),
            };

            const response = await EmailTemplateApi.getAllTemplates(params);
            setTemplates(response.data.data);
            setTotalPages(response.data.meta.lastPage);
        } catch (error) {
            console.error('Error fetching templates:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch email templates',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            setStatsLoading(true);
            const response = await EmailTemplateApi.getTemplateStats();
            setStats(response.data.data); // Fixed: was response.data, now response.data.data
        } catch (error) {
            console.error('Error fetching stats:', error);
            // Set fallback stats data when API fails
            setStats({
                total: 0,
                active: 0,
                draft: 0,
                inactive: 0,
                byType: {
                    order_confirmation: 0,
                    order_ready: 0,
                    order_cancelled: 0,
                    order_delivered: 0,
                    user_registration: 0,
                    user_welcome: 0,
                    password_reset: 0,
                    email_verification: 0,
                    loyalty_points_earned: 0,
                    loyalty_points_redeemed: 0,
                    merchant_registration: 0,
                    merchant_approval: 0,
                    merchant_rejection: 0,
                    promotional: 0,
                    newsletter: 0,
                    account_suspension: 0,
                    account_reactivation: 0,
                }
            });
            toast({
                title: 'Warning',
                description: 'Stats API failed. Using fallback data.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchTemplateTypes = async () => {
        try {
            const response = await EmailTemplateApi.getTemplateTypes();
            setTemplateTypes(response.data.data); // Fixed: was response.data, now response.data.data
        } catch (error) {
            console.error('Error fetching template types:', error);
            // Set fallback template types if API fails
            setTemplateTypes(DEFAULT_TEMPLATE_TYPES);
            toast({
                title: 'Warning',
                description: 'Using default template types. Some features may be limited.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const getTemplateVariables = async (type) => {
        try {
            const response = await EmailTemplateApi.getTemplateVariables(type);
            return response.data;
        } catch (error) {
            console.error('Error fetching template variables:', error);
            return [];
        }
    };

    // CRUD operations
    const handleSaveTemplate = async (templateData) => {
        try {
            setFormLoading(true);

            if (selectedTemplate) {
                // Optimistic update for edit
                const updatedTemplate = { ...selectedTemplate, ...templateData };
                setTemplates(prev => prev.map(t => 
                    t.id === selectedTemplate.id ? updatedTemplate : t
                ));

                await EmailTemplateApi.updateTemplate(selectedTemplate.id, templateData);
                toast({
                    title: 'Success',
                    description: 'Template updated successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                // Optimistic update for create
                const tempId = `temp_${Date.now()}`;
                const newTemplate = { ...templateData, id: tempId, createdAt: new Date().toISOString() };
                setTemplates(prev => [newTemplate, ...prev]);

                const response = await EmailTemplateApi.createTemplate(templateData);
                
                // Replace optimistic template with real response
                setTemplates(prev => prev.map(t => 
                    t.id === tempId ? response.data.data : t
                ));

                toast({
                    title: 'Success',
                    description: 'Template created successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            }

            closeForm();
            setSelectedTemplate(null);
            
            // Refresh data to ensure consistency
            await Promise.all([fetchTemplates(), fetchStats()]);
        } catch (error) {
            console.error('Error saving template:', error);
            
            // Rollback optimistic updates on error
            if (selectedTemplate) {
                // Revert edit changes
                fetchTemplates();
            } else {
                // Remove optimistic create
                setTemplates(prev => prev.filter(t => !t.id.toString().startsWith('temp_')));
            }
            
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to save template',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteTemplate = async () => {
        try {
            setDeleteLoading(true);
            
            // Optimistic update - remove template immediately
            const templateId = templateToDelete.id;
            setTemplates(prev => prev.filter(t => t.id !== templateId));
            
            await EmailTemplateApi.deleteTemplate(templateId);

            toast({
                title: 'Success',
                description: 'Template deleted successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            closeDelete();
            setTemplateToDelete(null);
            
            // Refresh data to ensure consistency
            await Promise.all([fetchTemplates(), fetchStats()]);
        } catch (error) {
            console.error('Error deleting template:', error);
            
            // Rollback optimistic update on error
            fetchTemplates();
            
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete template',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDuplicateTemplate = async (template) => {
        try {
            const newName = `${template.name} (Copy)`;
            
            // Optimistic update - add duplicated template immediately
            const tempId = `temp_${Date.now()}`;
            const duplicatedTemplate = { 
                ...template, 
                id: tempId, 
                name: newName,
                createdAt: new Date().toISOString(),
                usageCount: 0,
                isDefault: false
            };
            setTemplates(prev => [duplicatedTemplate, ...prev]);
            
            const response = await EmailTemplateApi.duplicateTemplate(template.id, newName);
            
            // Replace optimistic template with real response
            setTemplates(prev => prev.map(t => 
                t.id === tempId ? response.data.data : t
            ));

            toast({
                title: 'Success',
                description: 'Template duplicated successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Refresh data to ensure consistency
            await Promise.all([fetchTemplates(), fetchStats()]);
        } catch (error) {
            console.error('Error duplicating template:', error);
            
            // Rollback optimistic update on error
            setTemplates(prev => prev.filter(t => !t.id.toString().startsWith('temp_')));
            
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to duplicate template',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleSetDefault = async (template) => {
        try {
            // Optimistic update - update template immediately
            setTemplates(prev => prev.map(t => ({
                ...t,
                isDefault: t.id === template.id ? true : false
            })));
            
            await EmailTemplateApi.setAsDefault(template.id);

            toast({
                title: 'Success',
                description: 'Template set as default successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            // Refresh data to ensure consistency
            await fetchTemplates();
        } catch (error) {
            console.error('Error setting default:', error);
            
            // Rollback optimistic update on error
            fetchTemplates();
            
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to set as default',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const handleSendTestEmail = async (testData) => {
        try {
            setTestLoading(true);
            await EmailTemplateApi.sendTestEmail(selectedTemplate.id, testData);

            toast({
                title: 'Success',
                description: 'Test email sent successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            closeTest();
        } catch (error) {
            console.error('Error sending test email:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to send test email',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setTestLoading(false);
        }
    };

    // Event handlers
    const handleEditTemplate = (template) => {
        setSelectedTemplate(template);
        openForm();
    };

    const handleCreateTemplate = () => {
        setSelectedTemplate(null);
        openForm();
    };

    const handleTestEmail = (template) => {
        setSelectedTemplate(template);
        openTest();
    };

    const handleDeleteClick = (template) => {
        setTemplateToDelete(template);
        openDelete();
    };

    const handleRefresh = () => {
        fetchTemplates();
        fetchStats();
        fetchTemplateTypes();
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setTypeFilter('');
        setIsDefaultFilter('');
        setCurrentPage(1);
    };

    // Auto-refresh mechanism - DISABLED
    // useEffect(() => {
    //     const refreshInterval = setInterval(() => {
    //         // Only refresh if not currently loading and no modals are open
    //         if (!loading && !statsLoading && !isFormOpen && !isTestOpen && !isDeleteOpen) {
    //             fetchTemplates();
    //             fetchStats();
    //         }
    //     }, 30000); // Refresh every 30 seconds

    //     return () => clearInterval(refreshInterval);
    // }, [loading, statsLoading, isFormOpen, isTestOpen, isDeleteOpen]);

    // Effects
    useEffect(() => {
        fetchTemplates();
    }, [currentPage, limit, statusFilter, typeFilter, isDefaultFilter]);

    useEffect(() => {
        fetchStats();
        fetchTemplateTypes();
    }, []);

    useEffect(() => {
        const delayedSearch = setTimeout(() => {
            if (searchTerm !== '') {
                setCurrentPage(1);
                fetchTemplates();
            } else if (searchTerm === '') {
                fetchTemplates();
            }
        }, 500);

        return () => clearTimeout(delayedSearch);
    }, [searchTerm]);

    // Update the template types passed to components to use fallback
    const templateTypesWithFallback = getTemplateTypesWithFallback(templateTypes);

    return (
        <Box color={textColor} bg={bgColor} minH="100vh">
            <Box maxW="1400px" mx="auto" p={{ base: 4, md: 6 }}>
                <VStack spacing={6} align="stretch">
                {/* Header */}
                <EmailTemplateHeader
                    onGoBack={() => navigate(-1)}
                    onRefresh={handleRefresh}
                    onCreateTemplate={handleCreateTemplate}
                />

                {/* Stats Cards */}
                <StatsCards stats={stats} loading={statsLoading}/>

                {/* Filters */}
                <TemplateFilters
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    typeFilter={typeFilter}
                    setTypeFilter={setTypeFilter}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    isDefaultFilter={isDefaultFilter}
                    setIsDefaultFilter={setIsDefaultFilter}
                    templateTypes={templateTypesWithFallback}
                    onClearFilters={handleClearFilters}
                />

                {/* Templates Table */}
                <TemplateTable
                    templates={templates}
                    loading={loading}
                    templateTypes={templateTypesWithFallback}
                    onEdit={handleEditTemplate}
                    onTest={handleTestEmail}
                    onDuplicate={handleDuplicateTemplate}
                    onSetDefault={handleSetDefault}
                    onDelete={handleDeleteClick}
                />

                {/* Pagination */}
                {!loading && templates.length > 0 && (
                    <Box>
                        <Pagination
                            currentPage={currentPage}
                            itemsPerPage={limit}
                            totalPages={totalPages}
                            onItemsPerPageChange={(lim) => setLimit(lim)}
                            onPageChange={(page) => setCurrentPage(page)}
                        />
                    </Box>
                )}
                </VStack>
            </Box>

            {/* Modals */}
            <TemplateFormModal
                isOpen={isFormOpen}
                onClose={closeForm}
                template={selectedTemplate}
                onSave={handleSaveTemplate}
                templateTypes={templateTypesWithFallback}
                templateVariables={getTemplateVariables}
                loading={formLoading}
            />

            <TestEmailModal
                isOpen={isTestOpen}
                onClose={closeTest}
                template={selectedTemplate}
                onSendTest={handleSendTestEmail}
                loading={testLoading}
            />

            <DeleteConfirmationDialog
                isOpen={isDeleteOpen}
                onClose={closeDelete}
                onConfirm={handleDeleteTemplate}
                templateName={templateToDelete?.name || ""}
                loading={deleteLoading}
            />
        </Box>
    );
};
